/**
 * Custom Dropdown Component
 * Provides functionality for custom HTML dropdowns with full-width menus
 * 
 * IMPORTANT: This component handles duplicate mobile/desktop dropdowns and self-healing re-initialization.
 * 
 * Key Features:
 * - Only initializes VISIBLE dropdowns (skips hidden mobile/desktop duplicates)
 * - Self-healing: Dropdowns detect their own changes and trigger re-initialization
 * - Debounced re-initialization prevents race conditions
 * - Static event listeners for efficiency (one global listener for all dropdowns)
 * - Comprehensive logging for debugging (filter console by "CustomDropdown")
 * 
 * How It Works:
 * 1. On page load: Initializes only visible dropdowns (3 visible, 3 hidden)
 * 2. On variant selection: Dropdown updates hidden <select>, detects change, triggers re-init
 * 3. After 150ms: All dropdowns destroyed and recreated with fresh event listeners
 * 4. Repeat: Dropdowns always work because they're rebuilt after each variant change
 * 
 * See VARIANT_DROPDOWN_FIX_SUMMARY.md for full details.
 */

/**
 * Utility: Debounce function to prevent rapid re-initialization
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

class CustomDropdown {
  static openDropdowns = [];
  static isListenerAttached = false;
  static instanceCounter = 0;

  constructor(element) {
    this.instanceId = ++CustomDropdown.instanceCounter;
    this.element = element;
    this.trigger = element.querySelector('[data-dropdown-trigger]');
    this.menu = element.querySelector('[data-dropdown-menu]');
    this.selectedText = element.querySelector('.custom-dropdown__selected-text');
    this.hiddenSelect = element.querySelector('.custom-dropdown__hidden-select');
    this.options = element.querySelectorAll('.custom-dropdown__option');
    this.isOpen = false;
    this.init();
    CustomDropdown.attachGlobalListener();
  }

  static attachGlobalListener() {
    if (this.isListenerAttached) {
      return;
    }

    // Single global click handler for all dropdowns
    document.addEventListener('click', (e) => {
      this.openDropdowns.forEach(dropdown => {
        if (!dropdown.element.contains(e.target)) {
          dropdown.close();
        }
      });
    });

    // Single global escape key handler for all dropdowns
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.openDropdowns.forEach(dropdown => dropdown.close());
      }
    });

    this.isListenerAttached = true;
  }

  init() {
    if (!this.trigger || !this.menu) {
      console.warn(`[CustomDropdown #${this.instanceId}] Missing trigger or menu - initialization aborted`);
      return;
    }

    // Store bound methods for cleanup
    this.boundToggle = (e) => this.toggle(e);
    this.boundHandleKeydown = (e) => this.handleKeydown(e);

    // Event listeners
    this.trigger.addEventListener('click', this.boundToggle);
    this.trigger.addEventListener('keydown', this.boundHandleKeydown);
    
    // Set ARIA attributes for accessibility
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.menu.setAttribute('role', 'listbox');
    
    // Option click handlers
    this.optionHandlers = new Map();
    this.options.forEach((option, index) => {
      const boundSelectOption = (e) => this.selectOption(e);
      const boundHandleOptionKeydown = (e) => this.handleOptionKeydown(e);
      
      this.optionHandlers.set(option, { selectOption: boundSelectOption, handleKeydown: boundHandleOptionKeydown });
      
      option.addEventListener('click', boundSelectOption);
      option.addEventListener('keydown', boundHandleOptionKeydown);
      
      // Set ARIA attributes for accessibility
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.setAttribute('tabindex', '-1');
    });

    // Initialize selected state
    this.updateSelectedState();

    // Listen for changes from the variant system
    if (this.hiddenSelect) {
      this.hiddenSelect.addEventListener('change', (event) => {
        // Refresh our dropdown state when the variant system changes the select
        this.refreshFromSelect();
        
        // Check if this is a variant option (not size selector)
        const isVariantOption = !this.hiddenSelect.hasAttribute('data-size-selector');
        
        if (isVariantOption) {
          // Trigger re-initialization of all dropdowns after variant changes
          // This ensures other dropdowns update their available options
          if (typeof window.debouncedDropdownInitialize === 'function') {
            window.debouncedDropdownInitialize();
          }
        }
      });
    }
  }

  toggle(e) {
    e.preventDefault();
    e.stopPropagation();
    
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    // Close other dropdowns first
    CustomDropdown.openDropdowns.forEach(d => {
      if (d !== this) {
        d.close();
      }
    });
    
    this.isOpen = true;
    this.element.setAttribute('data-open', '');
    this.trigger.setAttribute('aria-expanded', 'true');
    
    // Add to the list of open dropdowns
    if (!CustomDropdown.openDropdowns.includes(this)) {
      CustomDropdown.openDropdowns.push(this);
    }
    
    // Focus first option
    const firstOption = this.menu.querySelector('.custom-dropdown__option:not([disabled])');
    if (firstOption) {
      firstOption.focus();
    }
  }

  close() {
    if (!this.isOpen) {
      return; // Prevent unnecessary work
    }
    
    this.isOpen = false;
    this.element.removeAttribute('data-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.focus();
    
    // Remove from the list of open dropdowns
    CustomDropdown.openDropdowns = CustomDropdown.openDropdowns.filter(d => d !== this);
  }

  selectOption(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const option = e.currentTarget;
    const value = option.dataset.value;
    const text = option.textContent.trim();
    
    if (option.disabled) {
      return;
    }

    // Update selected text
    this.selectedText.textContent = text;
    
    // Update hidden select (single consolidated block)
    if (this.hiddenSelect) {
      const optionElement = this.hiddenSelect.querySelector(`option[value="${value}"]`);
      if (optionElement) {
        // Clear previous selections
        this.hiddenSelect.querySelectorAll('option').forEach(opt => {
          opt.selected = false;
        });
        // Select new option
        optionElement.selected = true;
      }
      
      // Trigger change event for form handling
      const changeEvent = new Event('change', { bubbles: true });
      this.hiddenSelect.dispatchEvent(changeEvent);
      
      // Also trigger input event for better compatibility
      const inputEvent = new Event('input', { bubbles: true });
      this.hiddenSelect.dispatchEvent(inputEvent);
    }

    // Update visual state
    this.updateSelectedState();

    // Close dropdown
    this.close();

    // Small delay to ensure the variant system has processed the change
    setTimeout(() => {
      // Trigger custom event for variant selection
      const customEvent = new CustomEvent('dropdown:change', {
        detail: { value, text, option },
        bubbles: true
      });
      this.element.dispatchEvent(customEvent);

      // Special handling for size selector
      if (this.hiddenSelect && this.hiddenSelect.hasAttribute('data-size-selector')) {
        const sizeEvent = new CustomEvent('size:change', {
          detail: { value, text },
          bubbles: true
        });
        this.element.dispatchEvent(sizeEvent);
      }
    }, 50);
  }

  updateSelectedState() {
    // Remove selected class and ARIA from all options
    this.options.forEach(option => {
      option.classList.remove('custom-dropdown__option--selected');
      option.setAttribute('aria-selected', 'false');
    });

    // Add selected class and ARIA to current selection
    const selectedValue = this.hiddenSelect ? this.hiddenSelect.value : '';
    const selectedOption = Array.from(this.options).find(option => 
      option.dataset.value === selectedValue
    );
    
    if (selectedOption) {
      selectedOption.classList.add('custom-dropdown__option--selected');
      selectedOption.setAttribute('aria-selected', 'true');
    }
  }

  handleKeydown(e) {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.toggle(e);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!this.isOpen) {
          this.open();
        } else {
          this.focusNextOption();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (this.isOpen) {
          this.focusPreviousOption();
        }
        break;
    }
  }

  handleOptionKeydown(e) {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.selectOption(e);
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.focusNextOption();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.focusPreviousOption();
        break;
      case 'Escape':
        e.preventDefault();
        this.close();
        break;
    }
  }

  focusNextOption() {
    const currentFocus = document.activeElement;
    const allOptions = Array.from(this.options).filter(opt => !opt.disabled);
    const currentIndex = allOptions.indexOf(currentFocus);
    const nextIndex = (currentIndex + 1) % allOptions.length;
    allOptions[nextIndex].focus();
  }

  focusPreviousOption() {
    const currentFocus = document.activeElement;
    const allOptions = Array.from(this.options).filter(opt => !opt.disabled);
    const currentIndex = allOptions.indexOf(currentFocus);
    const prevIndex = currentIndex <= 0 ? allOptions.length - 1 : currentIndex - 1;
    allOptions[prevIndex].focus();
  }

  handleOutsideClick(e) {
    if (!this.element.contains(e.target)) {
      this.close();
    }
  }

  // Public method to set value programmatically
  setValue(value) {
    const option = Array.from(this.options).find(opt => opt.dataset.value === value);
    if (option && !option.disabled) {
      this.selectOption({ currentTarget: option, preventDefault: () => {}, stopPropagation: () => {} });
    }
  }

  // Public method to get current value
  getValue() {
    return this.hiddenSelect ? this.hiddenSelect.value : '';
  }

  // Public method to refresh the dropdown state from the hidden select
  refreshFromSelect() {
    if (this.hiddenSelect) {
      const selectedValue = this.hiddenSelect.value;
      const selectedOption = this.hiddenSelect.querySelector(`option[value="${selectedValue}"]`);
      if (selectedOption && selectedOption.textContent) {
        this.selectedText.textContent = selectedOption.textContent.trim();
        this.updateSelectedState();
      }
    }
  }

  // Public method to destroy the dropdown and clean up event listeners
  destroy() {
    // Close dropdown if open (this will also remove from openDropdowns array)
    if (this.isOpen) {
      this.close();
    }
    
    // Remove trigger event listeners
    if (this.trigger && this.boundToggle) {
      this.trigger.removeEventListener('click', this.boundToggle);
      this.trigger.removeEventListener('keydown', this.boundHandleKeydown);
    }

    // Remove option event listeners
    if (this.optionHandlers) {
      this.optionHandlers.forEach((handlers, option) => {
        option.removeEventListener('click', handlers.selectOption);
        option.removeEventListener('keydown', handlers.handleKeydown);
      });
      this.optionHandlers.clear();
    }
    
    // Note: Global document listeners are static and shared across all instances,
    // so they are NOT removed here. They will be cleaned up when all instances are destroyed.
  }
}

/**
 * Check if an element is visible in the DOM
 * Returns false if element or any parent has display: none
 */
function isElementVisible(element) {
  // Check if element exists
  if (!element) return false;
  
  // Check if element has offsetParent (null means it's hidden)
  // This covers display: none on the element or any parent
  if (element.offsetParent === null) {
    // Exception: elements with position: fixed don't have offsetParent
    const style = window.getComputedStyle(element);
    if (style.position !== 'fixed') {
      return false;
    }
  }
  
  // Check computed visibility and display
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }
  
  return true;
}

/**
 * Initialize all custom dropdowns on the page
 */
function initializeCustomDropdowns() {
  // Clean up any existing dropdowns that might be re-initialized
  const existingDropdowns = document.querySelectorAll('[data-initialized="true"]');
  
  existingDropdowns.forEach((dropdown) => {
    if (dropdown.customDropdownInstance) {
      dropdown.customDropdownInstance.destroy();
      delete dropdown.customDropdownInstance;
      dropdown.removeAttribute('data-initialized');
    }
  });

  // Initialize new dropdowns (only visible ones)
  const allDropdowns = document.querySelectorAll('[data-dropdown]:not([data-initialized])');
  const visibleDropdowns = Array.from(allDropdowns).filter(dropdown => isElementVisible(dropdown));
  
  visibleDropdowns.forEach((dropdown) => {
    dropdown.setAttribute('data-initialized', 'true');
    dropdown.customDropdownInstance = new CustomDropdown(dropdown);
  });
}

// Create a single debounced version of the initialization function
const debouncedInitialize = debounce(initializeCustomDropdowns, 150);

// Expose to window so dropdown instances can trigger re-initialization
window.debouncedDropdownInitialize = debouncedInitialize;

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCustomDropdowns);
} else {
  // DOM is already loaded
  initializeCustomDropdowns();
}

// Re-initialize dropdowns when variant sections are updated
// Subscribe to the theme's pub/sub system for variant changes
if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
  // Subscribe to option value selection changes
  subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (data) => {
    // Check if the event is from a variant-radios element (palette picker)
    // If so, skip re-initialization to prevent infinite loops
    const eventTarget = data?.data?.event?.target;
    if (eventTarget && eventTarget.closest('variant-radios')) {
      return;
    }
    
    debouncedInitialize();
  });
  
  // Subscribe to variant changes
  subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
    debouncedInitialize();
  });
} else {
  // Fallback to DOM events if pub/sub system is not available
  document.addEventListener('variant:change', () => {
    debouncedInitialize();
  });

  document.addEventListener('product:variant-change', () => {
    debouncedInitialize();
  });

  document.addEventListener('option-value-selection-change', () => {
    debouncedInitialize();
  });
}

// Export for use in other scripts
window.CustomDropdown = CustomDropdown;
